import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { MarkdownField } from "@/components/ui/markdown-field";
import { useToast } from "@/hooks/use-toast";
import { getScriptureNotes } from "@/lib/bible-api";
import type { ScriptureNote, ScriptureReference } from "@shared/types/supabase";
import { format } from "date-fns";
import { Loader2, RefreshCcw, Trash2 } from "lucide-react";
import { getScriptureReferences } from "@/lib/bible-api";
import { put, del } from "@/lib/api";

interface ScriptureNotesListProps {
  documentId?: number;
  refreshTrigger?: number;
}

export function ScriptureNotesList({ documentId, refreshTrigger }: ScriptureNotesListProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState<ScriptureNote[]>([]);
  const [references, setReferences] = useState<Record<number, ScriptureReference>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editedNoteContent, setEditedNoteContent] = useState("");
  
  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch notes
      const notesData = await getScriptureNotes(documentId);
      setNotes(notesData);
      
      // Fetch references
      const referencesData = await getScriptureReferences();
      const referencesMap: Record<number, ScriptureReference> = {};
      referencesData.forEach(ref => {
        referencesMap[ref.id] = ref;
      });
      setReferences(referencesMap);
    } catch (err) {
      console.error("Error fetching scripture notes:", err);
      setError("Failed to load notes");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [documentId, refreshTrigger]);
  
  const handleRefresh = () => {
    fetchData();
  };
  
  const handleEditNote = (note: ScriptureNote) => {
    setEditingNoteId(note.id);
    setEditedNoteContent(note.content);
  };
  
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedNoteContent("");
  };
  
  const handleSaveEdit = async (noteId: number) => {
    try {
      await put(`/notes/${noteId}`, { content: editedNoteContent });
      setNotes((prev) =>
        prev.map((note) =>
          note.id === noteId ? { ...note, content: editedNoteContent } : note,
        ),
      );

      setEditingNoteId(null);
      setEditedNoteContent("");

      toast({
        title: "Note updated",
        description: "Your note has been updated successfully.",
      });
    } catch (err) {
      console.error("Error updating note:", err);
      toast({
        title: "Failed to update note",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await del(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((note) => note.id !== noteId));

      toast({
        title: "Note deleted",
        description: "Your note has been deleted successfully.",
      });
    } catch (err) {
      console.error("Error deleting note:", err);
      toast({
        title: "Failed to delete note",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };
  
  const formatReferenceText = (note: ScriptureNote): string => {
    const refId = note.parent_id;
    const reference = refId != null ? references[refId] : undefined;
    if (!reference) return "Unknown reference";
    
    return `${reference.book} ${reference.chapter}:${reference.verse_start}${
      reference.verse_end > reference.verse_start ? '-' + reference.verse_end : ''
    }`;
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }
  
  const filteredNotes = notes;
  
  if (filteredNotes.length === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <p className="text-muted-foreground">No scripture notes yet.</p>
        <p className="text-sm text-muted-foreground">
          Open <span className="font-medium text-foreground">Saved</span>, tap{" "}
          <span className="font-medium text-foreground">Note</span> on a passage, then it will
          show up here. Use the top-level{" "}
          <span className="font-medium text-foreground">Notes</span> tab for document notes.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">
          {documentId ? "Document Scripture Notes" : "All Scripture Notes"}
        </h3>
        <Button variant="ghost" size="sm" onClick={handleRefresh}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      <div className="space-y-3">
        {filteredNotes.map(note => (
          <Card key={note.id}>
            <CardHeader className="py-3 px-4">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm font-medium">
                  {formatReferenceText(note)}
                </CardTitle>
                <CardDescription className="text-xs">
                  {format(new Date(note.created_at), "MMM d, yyyy")}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="py-3 px-4">
              {editingNoteId === note.id ? (
                <MarkdownField
                  value={editedNoteContent}
                  onValueChange={setEditedNoteContent}
                  className="mb-2"
                />
              ) : (
                <Markdown content={note.content} className="text-sm" />
              )}
            </CardContent>
            <CardFooter className="py-2 px-4 flex justify-end space-x-2 bg-muted/20">
              {editingNoteId === note.id ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => handleSaveEdit(note.id)}
                    disabled={!editedNoteContent.trim()}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEditNote(note)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteNote(note.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}